import { zodResolver } from '@hookform/resolvers/zod';
import { ColumnDef } from '@tanstack/react-table';
import {
  Action,
  Card,
  ConfirmModal,
  Modal,
  NumberInput,
  PageStatus,
  RelativeDateTime,
} from 'corso-admin-ui';
import { CommandAdminApi, commandPlatformApi } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import {
  useCreateUnit,
  useDeleteUnit,
  useProtectSetStores,
  useUpdateUnit,
} from '~/hooks/useProtectSets';
import { useIsSuperUser } from '~/providers/AuthenticatedUserProvider';
import { formatter } from 'corso-web-lib';
import { DataListCard, DataListEntry } from './DataList';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from './dataTable';

type PricingSetDetail = CommandAdminApi.ResponseBody<
  '/ship-protect-set/:setId',
  'get'
>;

type ProtectUnit = PricingSetDetail['units'][number];

type ProtectSetStore = CommandAdminApi.ResponseBody<
  '/ship-protect-set/:setId/store',
  'get'
>[number];

const { idColumn, nameColumn, shopifyDomainColumn, createdOnColumn } =
  sharedTableDefinitions;

const handleCurrency = (value?: number | null) =>
  value ? formatter.currency(value, 'USD') : '—';

const unitSchema =
  commandPlatformApi['/ship-protect-set/:setId/unit'].post.request.body;

type UnitFormValues = z.infer<typeof unitSchema>;

const optionalNumber = (value: string) =>
  value === '' ? undefined : Number.parseFloat(value);

function UnitForm({
  defaultValues,
  submitLabel,
  onSubmit,
}: {
  defaultValues?: Partial<UnitFormValues>;
  submitLabel: string;
  onSubmit: (values: UnitFormValues) => void;
}) {
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<UnitFormValues>({
    resolver: zodResolver(unitSchema),
    defaultValues,
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(onSubmit)(event).catch(console.error);
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={submitHandler}>
      <NumberInput
        id="chargeAmount"
        label="Charge Amount"
        required
        step="any"
        addon={{ insideStart: '$' }}
        {...register('chargeAmount', { valueAsNumber: true })}
        error={errors.chargeAmount?.message}
      />
      <NumberInput
        id="rangeStart"
        label="Range Start"
        required
        step="any"
        addon={{ insideStart: '$' }}
        {...register('rangeStart', { valueAsNumber: true })}
        error={errors.rangeStart?.message}
      />
      <NumberInput
        id="rangeEnd"
        label="Range End"
        required
        step="any"
        addon={{ insideStart: '$' }}
        {...register('rangeEnd', { valueAsNumber: true })}
        error={errors.rangeEnd?.message}
      />
      <NumberInput
        id="billMerchantAmount"
        label="Bill Merchant Amount"
        step="any"
        addon={{ insideStart: '$' }}
        {...register('billMerchantAmount', { setValueAs: optionalNumber })}
        error={errors.billMerchantAmount?.message}
      />
      <div className="flex justify-end">
        <Action variant="primary" type="submit">
          {submitLabel}
        </Action>
      </div>
    </form>
  );
}

function AddUnitModal({
  setId,
  onClose,
}: {
  setId: number;
  onClose: () => void;
}) {
  const { mutate: createUnit } = useCreateUnit(setId);

  return (
    <Modal show onClose={onClose} title="Add Unit">
      <UnitForm
        submitLabel="Add"
        onSubmit={(body) => createUnit(body, { onSuccess: onClose })}
      />
    </Modal>
  );
}

function EditUnitModal({
  setId,
  unit,
  onClose,
}: {
  setId: number;
  unit: ProtectUnit;
  onClose: () => void;
}) {
  const { mutate: updateUnit } = useUpdateUnit(setId);

  return (
    <Modal show onClose={onClose} title="Edit Unit">
      <UnitForm
        submitLabel="Save"
        defaultValues={{
          chargeAmount: unit.chargeAmount,
          rangeStart: unit.rangeStart,
          rangeEnd: unit.rangeEnd,
          billMerchantAmount: unit.billMerchantAmount,
        }}
        onSubmit={(body) =>
          updateUnit({ unitId: unit.id, body }, { onSuccess: onClose })
        }
      />
    </Modal>
  );
}

function PricingSetStores({ setId }: { setId: number }) {
  const navigate = useNavigate();
  const query = useProtectSetStores(setId);

  const storeColumns: ColumnDef<ProtectSetStore>[] = [
    idColumn<ProtectSetStore>({ meta: { align: 'left' } }),
    nameColumn<ProtectSetStore>(),
    shopifyDomainColumn<ProtectSetStore>(),
    createdOnColumn<ProtectSetStore>(),
  ];

  return (
    <Card>
      <Card.Line>
        <Card.Heading>Stores</Card.Heading>
      </Card.Line>
      {match(query)
        .with({ status: 'pending' }, () => (
          <PageStatus.Loading title="Loading stores" />
        ))
        .with({ status: 'error' }, () => (
          <PageStatus.Error title="Failed to load stores" error={query.error} />
        ))
        .with({ status: 'success', data: P.select() }, (stores) =>
          stores.length > 0 ? (
            <DataTable
              data={stores}
              columns={storeColumns}
              onRowClick={(store) =>
                navigate({
                  pathname: `/stores/${store.id}/info`,
                  search: new URLSearchParams({
                    returnTo: `/pricing-sets/${setId}`,
                  }).toString(),
                })
              }
            />
          ) : (
            <DataTableStatus
              icon="store"
              title="No stores found"
              details="No stores are assigned to this pricing set."
            />
          ),
        )
        .exhaustive()}
    </Card>
  );
}

export function PricingSet({
  detail,
  fromStore,
}: {
  detail: PricingSetDetail;
  fromStore?: boolean;
}) {
  const isSuperUser = useIsSuperUser();
  const [showAdd, setShowAdd] = useState(false);
  const [unitToEdit, setUnitToEdit] = useState<ProtectUnit | null>(null);
  const [editType, setEditType] = useState<'edit' | 'delete' | null>(null);

  const { mutate: deleteUnit, isPending: deletePending } = useDeleteUnit(
    detail.id,
  );

  const canSuperEdit = isSuperUser && detail.storeCount === 0;

  const closeEdit = () => {
    setUnitToEdit(null);
    setEditType(null);
  };

  const dataList = [
    {
      label: 'ID',
      value: `${detail.id}`,
      copyEnabled: true,
    },
    {
      label: 'Name',
      value: detail.name,
    },
    {
      label: 'Display Name',
      value: detail.displayName ?? '—',
    },
    {
      label: 'Description',
      value: detail.description ?? '—',
    },
    {
      label: 'Code',
      value: detail.code,
    },
    {
      label: 'Created On',
      value: detail.createdOn ? (
        <RelativeDateTime dateTime={detail.createdOn} />
      ) : (
        '—'
      ),
    },
  ] satisfies DataListEntry[];

  const superUserActions: ColumnDef<ProtectUnit>[] = canSuperEdit
    ? [
        {
          id: 'actions',
          header: '',
          meta: { align: 'right' },
          cell: ({ row }) => (
            <>
              <Action
                variant="ghost"
                onClick={() => {
                  setUnitToEdit(row.original);
                  setEditType('edit');
                }}
                accessibilityLabel="Edit Pricing Unit"
                icon="edit"
              />
              <Action
                variant="ghost"
                onClick={() => {
                  setUnitToEdit(row.original);
                  setEditType('delete');
                }}
                accessibilityLabel="Delete Pricing Unit"
                icon="remove"
              />
            </>
          ),
        },
      ]
    : [];

  const unitColumns: ColumnDef<ProtectUnit>[] = [
    idColumn<ProtectUnit>({ meta: { align: 'left' } }),
    {
      accessorKey: 'chargeAmount',
      header: 'Charge Amount',
      enableSorting: true,
      cell: ({ row }) => handleCurrency(row.original.chargeAmount),
    },
    {
      accessorKey: 'rangeStart',
      header: 'Range Start',
      enableSorting: true,
      cell: ({ row }) => handleCurrency(row.original.rangeStart),
    },
    {
      accessorKey: 'rangeEnd',
      header: 'Range End',
      enableSorting: true,
      cell: ({ row }) => handleCurrency(row.original.rangeEnd),
    },
    {
      accessorKey: 'billMerchantAmount',
      header: 'Bill Merchant Amount',
      cell: ({ row }) => handleCurrency(row.original.billMerchantAmount),
    },
    { accessorKey: 'sku', header: 'SKU' },
    ...superUserActions,
  ];

  return (
    <div className="flex flex-col gap-4">
      <DataListCard items={dataList} />
      <Card>
        <Card.Line>
          <Card.Heading>Units</Card.Heading>
          {canSuperEdit && (
            <Action
              icon="add"
              variant="ghost"
              onClick={() => setShowAdd(true)}
              accessibilityLabel={`Add unit to ${detail.name} pricing set`}
            />
          )}
        </Card.Line>
        <DataTable data={detail.units} columns={unitColumns} />
      </Card>

      {!fromStore && <PricingSetStores setId={detail.id} />}

      {showAdd && (
        <AddUnitModal setId={detail.id} onClose={() => setShowAdd(false)} />
      )}

      {unitToEdit && editType === 'edit' && (
        <EditUnitModal
          setId={detail.id}
          unit={unitToEdit}
          onClose={closeEdit}
        />
      )}

      <ConfirmModal
        show={editType === 'delete'}
        title="Delete unit"
        prompt="Delete this pricing unit? This cannot be undone."
        confirmText="Delete"
        variant="destructive"
        loading={deletePending}
        onCancel={closeEdit}
        onConfirm={() => {
          if (!unitToEdit) return;
          deleteUnit(unitToEdit.id, { onSuccess: closeEdit });
        }}
      />
    </div>
  );
}
